import { Component, inject, OnInit, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { AuthService } from '../../../../core/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule, MatLabel } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { LoaderComponent } from '../../../general/components/loader/loader.component';
import { HapticsService } from '../../../general/services/haptics.service';
import { SnackBarService } from '../../../services/snackbar.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { SeoService } from '../../../services/seo.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatLabel,
    MatIconModule,
    MatCardModule,
    LoaderComponent
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
})
export class ResetPasswordComponent implements OnInit {
  private authService = inject(AuthService);
  private haptics = inject(HapticsService);
  private gtm = inject(GtmService);
  private snackbar = inject(SnackBarService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private seo = inject(SeoService);
  private cdr = inject(ChangeDetectorRef); // ADDED: To force UI updates
  
  platformId = inject(PLATFORM_ID);

  token: string | null = null;
  email: string | null = null;
  loading = true;
  error: string | null = null;
  showForm = false;
  password = '';
  confirmPassword = '';

  ngOnInit(): void {
    this.seo.updateMetaTags('Reset Password', 'Reset Password for Horizinns', undefined, `${environment.appUrl}/login/reset-password`);

    // ONLY run token verification in the browser, never on the SSR server
    if (isPlatformBrowser(this.platformId)) {
      this.gtm.pageView({
        page_path: '/login/reset-password',
        page_title: 'Reset Password | Horizinns'
      });

      this.route.queryParams.subscribe(params => {
        this.token = params['token'] || null;

        if (!this.token) {
          this.error = 'No token provided. Please use the link from your email.';
          this.loading = false;
          this.cdr.detectChanges(); // Force UI to drop the loader
          return;
        }

        // Verify token with backend
        this.verifyToken(this.token);
      });
    } else {
      // If rendering on the server, drop the loader so it doesn't hang the page build
      this.loading = false;
    }
  }
  
  verifyToken(token: string): void {
    // We removed the pipe(catchError) here because auth.service already catches and formats the error
    this.authService.verifyPasswordResetToken(token).subscribe({
      next: (response) => {
        this.loading = false;
        
        if (response?.email) {
          this.email = response.email;
          this.showForm = true;
          this.error = null;
        } else {
          this.error = 'Invalid response from server.';
        }
        
        this.cdr.detectChanges(); // Force UI to show the form
      },
      error: (err) => {
        this.loading = false;
        // Read the clean error message thrown by auth.service
        this.error = err.message || 'Failed to verify token. Please try again later.';
        this.cdr.detectChanges(); // Force UI to show the error text
      }
    });
  }

  submitNewPassword() {
    this.haptics.click();

    if (this.password !== this.confirmPassword) {
      this.error = 'Passwords do not match.';
      this.snackbar.open(this.error, ' ', 
          { 
            duration: 5000, 
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
      return;
    }
   
    if (this.token === null) {
      this.snackbar.open('Your token has expired. Please request a new one.', ' ', 
          { 
            duration: 5000, 
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
    } else {
      this.authService.resetPassword(this.token, this.password).subscribe({
        next: () => {
          this.snackbar.open('Your password has been updated successfully', ' ', 
            { 
              duration: 5000, 
              panelClass: ['success-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          this.router.navigate(['/login']);
        },
        error: () => {
          this.snackbar.open('Your password was not updated successfully', ' ', 
            { 
              duration: 5000, 
              panelClass: ['error-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
        }
      });
    }
  }
}